import { Component, effect, input, Input, model, signal } from '@angular/core';
import { BrainParameter, ParamsGroup, ParamsSubGroup } from '../../services/brain-parameters.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ClipboardService } from '@shared/services/clipboard.service';
import { NgClass, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HelpComponent } from '../help/help.component';
import { BrainParamVisibilityComponent } from '../label/brain-param-visibility/brain-param-visibility.component';
import { SwitchComponent } from '../switch/switch.component';
import { TranslateLabelPipe } from '../../pipe/filter.pipe';
import _ from 'lodash';

@Component({
  selector: 'app-brain-parameters',
  templateUrl: './brain-parameters.component.html',
  styleUrl: './brain-parameters.component.scss',
  imports: [
    NgIf,
    FormsModule,
    NgClass,
    HelpComponent,
    BrainParamVisibilityComponent,
    SwitchComponent,
    TranslatePipe,
    TranslateLabelPipe,
  ],
})
export class BrainParametersComponent {
  @Input() editMode: boolean = false;
  @Input() enableSearch: boolean = true;
  @Input() enableFilterOverrides: boolean = true;
  @Input() disabled: boolean = false;
  @Input() showVisibility: boolean = false;
  @Input() showUids: boolean = false;
  paramGroups = input.required<ParamsGroup[]>();
  brainOverrides = input.required<Record<string, any>>();
  hasError = model<boolean>();

  search = signal('');
  protected filterOverrides = signal(false);

  groups: ParamsGroup[];
  groupsVM = new Map<number, GroupVM>();
  subGroupsVM = new Map<string, DriveVM>();
  errors = new Set<string>();
  modified = new Set<string>();

  constructor(
    protected tr: TranslateService,
    private clipboard: ClipboardService,
  ) {
    effect(() => {
      this.groupsVM.clear();
      this.subGroupsVM.clear();
      const overrides = this.brainOverrides();
      this.groups = this.paramGroups()
        .map(group => ({
          ...group,
          subGroups: group.subGroups
            .filter(subGroup => subGroup.params?.length)
            .map((subGroup) => ({
              ...subGroup,
              params: _.orderBy(subGroup.params, ['type', 'label'], ['asc', 'desc']),
            })),
        }))
        .filter(group => group.subGroups?.length);
      this.paramGroups().forEach((group) => {
        group.subGroups.forEach((subGroup) => {
          this.subGroupsVM.set(subGroup.uid, { dirty: this.hasOverride(subGroup, overrides) });
        });
        this.groupsVM.set(group.id, {
          opened: true,
          countModifiedDrives: group.subGroups.filter((subGroup) => this.hasOverride(subGroup, overrides))?.length ?? 0,
        });
      });
    });
  }

  hasOverride(subGroup: ParamsSubGroup, overrides: Record<string, any>) {
    return subGroup.params?.some((p) => this.isOverride(p, overrides));
  }

  isOverride(brainParameter: BrainParameter, overrides: Record<string, any>): boolean {
    const override = overrides[brainParameter.uid];
    return override !== null && override !== undefined && override !== '';
  }

  toggleOpenGroup(group: ParamsGroup) {
    this.groupsVM.get(group.id).opened = !this.groupsVM.get(group.id).opened;
  }

  driveFilter(subGroup: ParamsSubGroup): boolean {
    const search = this.search().toLocaleLowerCase();
    return (
      subGroup.params &&
      (!search ||
        subGroup.label?.toLocaleLowerCase()?.includes(search) ||
        subGroup.description?.toLocaleLowerCase()?.includes(search) ||
        subGroup.uid?.toLocaleLowerCase()?.includes(search)) &&
      (!this.filterOverrides() ||
        subGroup.params.some(
          (param) => this.brainOverrides()[param.uid] !== null && this.brainOverrides()[param.uid] !== undefined,
        ))
    );
  }

  onInputChange(element, brainTunable: BrainParameter) {
    const value = (element.value = element.value.replace(',', '.'));

    if (
      !/^\d*(\.\d*)?$/g.test(value) ||
      (value != '' && (value < brainTunable.min || value > brainTunable.max || value.slice(-1) == '.'))
    ) {
      this.errors.add(brainTunable.uid);
    } else {
      this.errors.delete(brainTunable.uid);
    }

    this.modified.add(brainTunable.uid);
    this.hasError.set(this.errors.size > 0);
  }

  protected copyActorToClipboard(actor: string) {
    this.clipboard.copy(actor, 'actorCopied');
  }
}

type GroupVM = {
  opened: boolean;
  countModifiedDrives: number;
};

type DriveVM = {
  dirty: boolean;
};
